import { attachAvatarVoice } from '../services/avatarVoice';
import { attachSafetyVoice } from '../services/safetyVoice';
import React, { useEffect, useState, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { Room, RoomEvent, Track, Participant, ConnectionState, DisconnectReason } from 'livekit-client';
import { attachScreenShareToRoom, detachScreenShareRoom } from '../services/screenShareService';
import { Mic, MicOff, Volume2 } from 'lucide-react';
import './VoiceChat.css';
import { setVoiceStatus, useVoiceStore, isMicPermissionError, MIC_BLOCKED_MESSAGE } from '../stores/voiceStore';

// LiveKit configuration
export const LIVEKIT_URL = import.meta.env.VITE_LIVEKIT_URL || '';

interface VoiceChatProps {
  roomName: string;
  userName: string;
  token: string;
  minimal?: boolean;
  listenOnly?: boolean;
  /** Connect with the mic off (Unmute turns it on) — used when joining just
   *  to present or watch a screen share. */
  startMuted?: boolean;
  onMuteChange?: (isMuted: boolean) => void;
  onSpeakingChange?: (isSpeaking: boolean) => void;
  /** Render the minimal mic control into this element (editor top bar). */
  slot?: HTMLElement | null;
}

const VoiceChat: React.FC<VoiceChatProps> = ({ roomName, userName, token, minimal = false, listenOnly = false, startMuted = false, onMuteChange, onSpeakingChange, slot }) => {
  const [room, setRoom] = useState<Room | null>(null);
  const [participants, setParticipants] = useState<Participant[]>([]);
  const [isConnected, setIsConnected] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [audioEnabled, setAudioEnabled] = useState(true);
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [connectionError, setConnectionError] = useState<string | null>(null);
  const [connectionState, setConnectionState] = useState<ConnectionState>(ConnectionState.Disconnected);
  // Safari / iOS / Quest block playback until a gesture that happens *after* connect.
  const [needsAudioUnlock, setNeedsAudioUnlock] = useState(false);

  const unlockAudio = useCallback(() => {
    room?.startAudio()
      .then(() => setNeedsAudioUnlock(!room.canPlaybackAudio))
      .catch((e) => console.warn('[VoiceChat] startAudio failed:', e));
  }, [room]);

  // Surface connection state to non-DOM UIs (the VR menu)
  useEffect(() => {
    window.dispatchEvent(new CustomEvent('voiceConnectionChanged', { detail: { state: connectionState } }));
  }, [connectionState]);

  // …and to the shared voice store, which UIs mounted later (the in-headset
  // menu) read directly.
  useEffect(() => {
    const phase = connectionState === ConnectionState.Connected ? 'connected'
      : connectionState === ConnectionState.Reconnecting || connectionState === ConnectionState.SignalReconnecting ? 'reconnecting'
      : connectionError ? 'error' : 'connecting';
    setVoiceStatus({ phase, error: connectionState === ConnectionState.Connected ? null : connectionError });
  }, [connectionState, connectionError]);
  useEffect(() => { setVoiceStatus({ muted: isMuted }); }, [isMuted]);
  useEffect(() => { setVoiceStatus({ needsAudioUnlock: needsAudioUnlock && isConnected }); }, [needsAudioUnlock, isConnected]);
  useEffect(() => () => {
    // Unmounted (left the room): back to joinable if a wrapper remains.
    useVoiceStore.setState((s) => ({ phase: s.mounts ? 'idle' : 'unavailable', needsAudioUnlock: false, anySpeaking: false }));
  }, []);

  // Unlock requests (Enter VR click, VR mic button): retry remote playback.
  useEffect(() => {
    if (!room) return;
    const onUnlock = () => {
      room.startAudio()
        .then(() => setNeedsAudioUnlock(!room.canPlaybackAudio))
        .catch(() => {});
    };
    window.addEventListener('gathrxr:voice-unlock', onUnlock);
    return () => window.removeEventListener('gathrxr:voice-unlock', onUnlock);
  }, [room]);

  // Hand the room to the screen-share service while connected
  useEffect(() => {
    if (room && connectionState === ConnectionState.Connected) {
      attachScreenShareToRoom(room);
      const detachVoice = attachAvatarVoice(room);
      return () => { detachVoice(); detachScreenShareRoom(room); };
    }
  }, [room, connectionState]);

  // Safety: muted / blocked people's voices are unsubscribed (live updates).
  useEffect(() => {
    if (room && connectionState === ConnectionState.Connected) return attachSafetyVoice(room);
  }, [room, connectionState]);

  // Define toggleMute early so it can be used in useEffect
  const toggleMute = useCallback(async () => {
    if (!room) return;
    
    const newMutedState = !isMuted;
    setIsMuted(newMutedState);
    onMuteChange?.(newMutedState);
    
    // Emit custom event for overlay
    const event = new CustomEvent('voiceMuteChanged', { detail: { isMuted: newMutedState } });
    window.dispatchEvent(event);
    
    try {
      await room.localParticipant.setMicrophoneEnabled(!newMutedState);
      setAudioEnabled(!newMutedState);
      if (!newMutedState) {
        setVoiceStatus({ micBlocked: false });
        setConnectionError((prev) => (prev === MIC_BLOCKED_MESSAGE ? null : prev));
      }
    } catch (error) {
      console.error('Error toggling microphone:', error);
      // Revert state if failed
      setIsMuted(!newMutedState);
      onMuteChange?.(!newMutedState);
      window.dispatchEvent(new CustomEvent('voiceMuteChanged', { detail: { isMuted: !newMutedState } }));
      if (!newMutedState && isMicPermissionError(error)) {
        setVoiceStatus({ micBlocked: true });
        setConnectionError(MIC_BLOCKED_MESSAGE);
      }
    }
  }, [room, isMuted, onMuteChange]);

  // Set up speaking detection
  useEffect(() => {
    if (!room || !room.localParticipant) return;

    const handleSpeakingChanged = () => {
      const speaking = room.localParticipant.isSpeaking;
      setIsSpeaking(speaking);
      onSpeakingChange?.(speaking);
      
      // Emit custom event for overlay
      const event = new CustomEvent('voiceSpeakingChanged', { detail: { isSpeaking: speaking } });
      window.dispatchEvent(event);
    };

    // Listen for speaking changes
    room.localParticipant.on('isSpeakingChanged', handleSpeakingChanged);

    return () => {
      room.localParticipant.off('isSpeakingChanged', handleSpeakingChanged);
    };
  }, [room, onSpeakingChange]);

  // Listen for toggleMute events
  useEffect(() => {
    const handleToggleMute = () => {
      toggleMute();
    };

    window.addEventListener('toggleMute', handleToggleMute);
    return () => {
      window.removeEventListener('toggleMute', handleToggleMute);
    };
  }, [toggleMute]);

  useEffect(() => {
    // Validate inputs first
    if (!token) {
      console.error('[VoiceChat] No token provided');
      setConnectionError('No authentication token provided');
      return;
    }

    if (!LIVEKIT_URL) {
      console.error('[VoiceChat] No LiveKit URL configured');
      setConnectionError('Voice chat service not configured');
      return;
    }

    console.log('[VoiceChat] Initializing connection to:', LIVEKIT_URL);
    console.log('[VoiceChat] Room:', roomName, 'User:', userName);

    let isComponentMounted = true;
    let connectionAttempted = false;

    const room = new Room({
      // Off: adaptive stream pauses any remote video that isn't attached to a
      // visible <video> element. Screen shares are drawn as a 3D texture, so
      // viewers (desktop and VR) would never receive a frame.
      adaptiveStream: false,
      dynacast: true,
      publishDefaults: {
        simulcast: false,
      },
    });

    // Enhanced event handling
    room.on(RoomEvent.Connected, () => {
      console.log('[VoiceChat] Connected to room:', room.name);
      if (isComponentMounted) {
        setIsConnected(true);
        setConnectionError(null);
        setConnectionState(ConnectionState.Connected);
        setParticipants(Array.from(room.remoteParticipants.values()));
      }
    });

    room.on(RoomEvent.Disconnected, (reason?: DisconnectReason) => {
      console.log('[VoiceChat] Disconnected from room. Reason:', reason);
      setVoiceStatus({ anySpeaking: false });
      if (isComponentMounted) {
        setIsConnected(false);
        setConnectionState(ConnectionState.Disconnected);
        if (reason && reason !== DisconnectReason.CLIENT_INITIATED) {
          setConnectionError(`Connection lost: ${reason}`);
        }
      }
    });

    room.on(RoomEvent.Reconnecting, () => {
      console.log('[VoiceChat] Reconnecting...');
      if (isComponentMounted) {
        setConnectionState(ConnectionState.Reconnecting);
      }
    });

    room.on(RoomEvent.Reconnected, () => {
      console.log('[VoiceChat] Reconnected');
      if (isComponentMounted) {
        setConnectionError(null);
        setConnectionState(ConnectionState.Connected);
      }
    });

    room.on(RoomEvent.ConnectionQualityChanged, (quality, participant) => {
      console.log('[VoiceChat] Connection quality changed:', quality, 'for', participant?.identity || 'local');
    });

    room.on(RoomEvent.ParticipantConnected, (participant) => {
      console.log('[VoiceChat] Participant connected:', participant.identity);
      if (isComponentMounted) {
        setParticipants(prev => [...prev, participant]);
      }
    });

    room.on(RoomEvent.ParticipantDisconnected, (participant) => {
      console.log('[VoiceChat] Participant disconnected:', participant.identity);
      if (isComponentMounted) {
        setParticipants(prev => prev.filter(p => p.sid !== participant.sid));
      }
    });

    room.on(RoomEvent.LocalTrackPublished, (publication, participant) => {
      console.log('[VoiceChat] Local track published:', publication.trackName);
    });

    room.on(RoomEvent.LocalTrackUnpublished, (publication, participant) => {
      console.log('[VoiceChat] Local track unpublished:', publication.trackName);
    });

    // Remote audio elements must live in the document: detached <audio> elements
    // are not reliably played by Safari/iOS/Quest browsers.
    const attachAudio = (track: Track) => {
      // Screen-share sound is played by screenShareService; attaching it here
      // too played it twice, slightly out of step (echo / lag).
      if (track.source === Track.Source.ScreenShareAudio) return;
      // TrackSubscribed can fire before connect() resolves, and the
      // "already in the room" pass below then attached the same track again:
      // two <audio> elements = every voice played twice.
      if (track.attachedElements.length > 0) return;
      const el = track.attach();
      el.dataset.voiceChat = 'remote';
      document.body.appendChild(el);
    };

    room.on(RoomEvent.TrackSubscribed, (track, publication, participant) => {
      console.log('[VoiceChat] Track subscribed:', track.kind, 'from', participant.identity);
      if (track.kind === Track.Kind.Audio) attachAudio(track);
    });

    room.on(RoomEvent.TrackUnsubscribed, (track, publication, participant) => {
      console.log('[VoiceChat] Track unsubscribed:', track.kind, 'from', participant.identity);
      track.detach().forEach((el) => el.remove());
    });

    room.on(RoomEvent.AudioPlaybackStatusChanged, () => {
      if (isComponentMounted) setNeedsAudioUnlock(!room.canPlaybackAudio);
    });

    // Anyone talking (you included): world music ducks under voices.
    room.on(RoomEvent.ActiveSpeakersChanged, (speakers) => {
      setVoiceStatus({ anySpeaking: speakers.length > 0 });
    });

    room.on(RoomEvent.RoomMetadataChanged, (metadata) => {
      console.log('[VoiceChat] Room metadata changed:', metadata);
    });

    // Connection with enhanced error handling
    if (isComponentMounted) {
      setConnectionState(ConnectionState.Connecting);
      connectionAttempted = true;
    }
    
    room.connect(LIVEKIT_URL, token, {
      autoSubscribe: true,
    }).then(() => {
      console.log('[VoiceChat] Room connection established');
      if (isComponentMounted) {
        setConnectionError(null);

        // Attach audio tracks from participants already in the room
        room.remoteParticipants.forEach((participant) => {
          participant.audioTrackPublications.forEach((publication) => {
            if (publication.track && publication.isSubscribed) {
              console.log('[VoiceChat] Attaching existing audio track from', participant.identity);
              attachAudio(publication.track);
            }
          });
        });

        // The Join click was spent on the token fetch, so playback may still be
        // locked; if so, AudioPlaybackStatusChanged shows the unlock button.
        room.startAudio().catch(() => {});
        setNeedsAudioUnlock(!room.canPlaybackAudio);

        // Enable microphone after successful connection (skip in listen-only mode)
        if (startMuted && !listenOnly) {
          setIsMuted(true);
          setAudioEnabled(false);
          onMuteChange?.(true);
          window.dispatchEvent(new CustomEvent('voiceMuteChanged', { detail: { isMuted: true } }));
          console.log('[VoiceChat] Joined muted — Unmute to talk');
        } else if (!listenOnly) {
          room.localParticipant.setMicrophoneEnabled(true).then(() => {
            if (isComponentMounted) {
              console.log('[VoiceChat] Microphone enabled');
              setVoiceStatus({ micBlocked: false });
              window.dispatchEvent(new CustomEvent('voiceMuteChanged', { detail: { isMuted: false } }));
            }
          }).catch(err => {
            console.warn('[VoiceChat] Failed to enable microphone:', err);
            // Still connected (others can be heard) — show the mic as off and
            // say why, rather than claiming it's live.
            if (isComponentMounted) {
              setIsMuted(true);
              setAudioEnabled(false);
              onMuteChange?.(true);
              window.dispatchEvent(new CustomEvent('voiceMuteChanged', { detail: { isMuted: true } }));
              if (isMicPermissionError(err)) {
                setVoiceStatus({ micBlocked: true });
                setConnectionError(MIC_BLOCKED_MESSAGE);
              }
            }
          });
        } else {
          console.log('[VoiceChat] Listen-only mode — microphone disabled');
        }
      }
    }).catch(error => {
      console.error('[VoiceChat] Connection failed:', error);
      if (isComponentMounted) {
        let errorMessage = 'Failed to connect to voice chat';
        
        if (error.message?.includes('token')) {
          errorMessage = 'Invalid authentication token. Please try refreshing the page.';
        } else if (error.message?.includes('room')) {
          errorMessage = 'Could not join voice chat room. Room may not exist.';
        } else if (error.message?.includes('network') || error.message?.includes('websocket')) {
          errorMessage = 'Network connection failed. Please check your internet connection.';
        } else if (error.message?.includes('permission')) {
          errorMessage = 'Permission denied. Please check microphone permissions.';
        }
        
        setConnectionError(errorMessage);
        setConnectionState(ConnectionState.Disconnected);
      }
    });

    setRoom(room);
    if (import.meta.env.DEV) (window as any).__gxVoiceRoom = room;

    return () => {
      console.log('[VoiceChat] Cleaning up room connection, state:', room.state);
      isComponentMounted = false;
      
      // Disconnect in every state: a room still connecting would otherwise be
      // leaked and finish joining after unmount.
      try {
        room.disconnect();
      } catch (error) {
        console.warn('[VoiceChat] Error during disconnect:', error);
      }
      document.querySelectorAll('audio[data-voice-chat="remote"]').forEach((el) => el.remove());
    };
  }, [token, roomName, userName, listenOnly]);

  if (minimal && slot) {
    const state = !isConnected ? 'connecting' : isMuted ? 'muted' : 'live';
    return createPortal(
      <div className="gx-voice-slotted">
        <button
          className={`btn-glass gx-voice-mic ${state}`}
          onClick={toggleMute}
          disabled={!isConnected}
          title={connectionError || (state === 'connecting' ? 'Connecting to voice…' : isMuted ? 'You are muted. Click to talk' : 'Your mic is on. Click to mute')}
        >
          {state === 'connecting' ? <Volume2 size={14} /> : isMuted ? <MicOff size={14} /> : <Mic size={14} />}
          <span>{state === 'connecting' ? 'Connecting…' : isMuted ? 'Muted' : 'Mic on'}</span>
        </button>
        {needsAudioUnlock && isConnected && (
          <button className="gx-voice-pop gx-voice-pop-btn" onClick={unlockAudio}><Volume2 size={14} /> Click to hear others</button>
        )}
        {connectionError && <div className="gx-voice-pop gx-voice-pop-err">{connectionError}</div>}
      </div>,
      slot,
    );
  }

  if (minimal) {
    return (
      <div className="gx-voice-float" style={{ position: 'fixed', bottom: '100px', right: '20px', zIndex: 1000 }}>
        <button
          className="voice-toggle-btn"
          onClick={toggleMute}
          disabled={!isConnected}
          title={connectionError || (!isConnected ? 'Connecting...' : (isMuted ? 'Unmute' : 'Mute'))}
          style={{
            width: '48px',
            height: '48px',
            borderRadius: '50%',
            backgroundColor: !isConnected ? 'rgba(128, 128, 128, 0.9)' : (isMuted ? 'rgba(255, 59, 48, 0.9)' : 'rgba(0, 199, 60, 0.9)'),
            border: connectionError ? '2px solid red' : 'none',
            color: 'white',
            cursor: isConnected ? 'pointer' : 'not-allowed',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          {!isConnected ? <Volume2 size={20} /> : (isMuted ? <MicOff size={20} /> : <Mic size={20} />)}
        </button>
        {needsAudioUnlock && isConnected && (
          <button className="voice-unlock-btn floating" onClick={unlockAudio}>
            <Volume2 size={16} /> Tap to hear others
          </button>
        )}
        {connectionError && (
          <div style={{
            position: 'absolute',
            bottom: '55px',
            right: '0',
            backgroundColor: 'rgba(255, 0, 0, 0.9)',
            color: 'white',
            padding: '8px 12px',
            borderRadius: '8px',
            fontSize: '12px',
            maxWidth: '200px',
            wordWrap: 'break-word'
          }}>
            {connectionError}
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="voice-chat-container">
      <div className="voice-chat-header">
        <Volume2 size={16} />
        <span>Voice Chat {
          connectionState === ConnectionState.Connected ? '• Connected' : 
          connectionState === ConnectionState.Connecting ? '• Connecting...' :
          connectionState === ConnectionState.Reconnecting ? '• Reconnecting...' :
          '• Disconnected'
        }</span>
      </div>
      
      {connectionError && (
        <div className="voice-error-banner" style={{
          backgroundColor: 'rgba(255, 0, 0, 0.1)',
          border: '1px solid red',
          borderRadius: '4px',
          padding: '8px',
          margin: '8px 0',
          color: 'red',
          fontSize: '12px'
        }}>
          {connectionError}
        </div>
      )}
      
      {needsAudioUnlock && isConnected && (
        <button className="voice-unlock-btn" onClick={unlockAudio}>
          <Volume2 size={16} /> Tap to hear others
        </button>
      )}

      <div className="voice-participants">
        {participants.map((participant) => (
          <div key={participant.sid} className="voice-participant">
            <div className={`voice-indicator ${participant.isSpeaking ? 'speaking' : ''}`} />
            <span>{participant.name || participant.identity || 'Anonymous'}</span>
          </div>
        ))}
        {room?.localParticipant && (
          <div className="voice-participant">
            <div className={`voice-indicator ${room.localParticipant.isSpeaking ? 'speaking' : ''}`} />
            <span>{userName} (You)</span>
          </div>
        )}
      </div>
      
      <div className="voice-controls">
        <button
          className={`voice-control-btn ${isMuted ? 'muted' : ''}`}
          onClick={toggleMute}
        >
          {isMuted ? <MicOff size={16} /> : <Mic size={16} />}
          <span>{isMuted ? 'Unmute' : 'Mute'}</span>
        </button>
      </div>
    </div>
  );
};

export default VoiceChat;